import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { cn } from '@/components/ui';
import type { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Markdown Renderer Component
 * 
 * Renders markdown content with support for tables and other GitHub Flavored Markdown features.
 */
export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  // Check message types
  const isUserMessage = className.includes('user-message');
  const isAssistantMessage = className.includes('assistant-message');
  
  // Preprocess content to fix HTML nesting issues
  // Add empty lines around code blocks to ensure they're not nested in paragraphs
  // Also add a special marker to help identify code blocks
  const processedContent = content.replace(
    /```([a-z]*)\n([\s\S]*?)```/g, 
    (match, lang, code) => `\n\n\`\`\`${lang}\n${code}\`\`\`\n\n`
  );
  
  return (
    <div className={cn('prose prose-sm max-w-none dark:prose-invert', className)}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]} 
        rehypePlugins={[rehypeSanitize]}
        components={{
          // Custom table rendering
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4" style={{ maxWidth: '100%' }}>
              <table className={cn(
                "border-collapse w-full text-sm text-foreground"
              )} {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-muted/50" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody className="divide-y divide-border" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="m-0 border-t p-0 even:bg-muted/20 border-border" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className="border border-border px-4 py-2 text-left font-semibold" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-border px-4 py-2 text-left" {...props} />
          ),
          // Improved implementation for code blocks and inline code
          code: function Code({ className, inline, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            
            if (inline) {
              // This is inline code (single backticks)
              return (
                <code 
                  className="text-sm font-mono bg-muted/50 px-1 py-0.5 rounded text-foreground" 
                  {...props}
                >
                  {children}
                </code>
              );
            }
            
            // This is a code block (triple backticks)
            return (
              <code className="text-sm font-mono" {...props}>
                {children}
              </code>
            );
          },
          // Custom pre rendering to avoid nesting issues
          pre: function Pre({ children, ...props }: any) {
            // Extract language from className if it exists
            const codeChild = React.Children.toArray(children).find(
              (child: any) => React.isValidElement(child) && child.type === 'code'
            ) as React.ReactElement | undefined;
            
            // Use type assertion to safely access className
            const codeProps = codeChild?.props as { className?: string } || {};
            const className = codeProps.className || '';
            const match = /language-(\w+)/.exec(className);
            
            return (
              <div className="relative my-4 rounded-md overflow-hidden">
                {/* Language tag */}
                {match && (
                  <div className="absolute top-0 right-0 px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-bl">
                    {match[1]}
                  </div>
                )}
                
                <pre className="p-4 pt-6 bg-muted/50 rounded-md overflow-x-auto text-foreground" {...props}>
                  {children}
                </pre>
              </div>
            );
          },
          // Custom paragraph rendering to handle potential code blocks
          p: ({ node, children, ...props }: any) => {
            // Check if this paragraph contains only a pre element
            const containsOnlyPre = React.Children.toArray(children).every(
              (child: any) => {
                if (typeof child === 'string') {
                  return child.trim() === '';
                }
                return React.isValidElement(child) && child.type === 'pre';
              }
            );
            
            // If it contains only a pre element, render without a paragraph wrapper
            if (containsOnlyPre) {
              return <>{children}</>;
            }
            
            // Otherwise, render as a normal paragraph
            return <p className="mb-4 last:mb-0 text-foreground" {...props}>{children}</p>;
          },
          // Custom heading rendering
          h1: ({ node, ...props }) => (
            <h1 className="text-2xl font-bold mt-6 mb-4 text-foreground" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold mt-6 mb-3 text-foreground" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-lg font-bold mt-5 mb-2 text-foreground" {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className="text-base font-bold mt-4 mb-2 text-foreground" {...props} />
          ),
          // Custom list rendering
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-6 mb-4 text-foreground" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-6 mb-4 text-foreground" {...props} />
          ),
          // Custom link rendering
          a: ({ node, ...props }) => (
            <a className="text-primary underline hover:text-primary/80" {...props} />
          ),
          // Custom blockquote rendering
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-border pl-4 italic my-4 text-muted-foreground" {...props} />
          ),
          // Custom hr rendering
          hr: ({ node, ...props }) => (
            <hr className="my-6 border-t border-border" {...props} />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
} 